import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowUpRight,
  Award,
  Factory,
  Building2,
  Download,
  HeartHandshake,
  Leaf,
  Mail,
  MapPin,
  Package,
  Phone,
  Recycle,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  Wrench,
} from 'lucide-react';
import AboutHero from './AboutHero';
import ScrollRevealSection from './ScrollRevealSection';
import {
  aboutScrollViewport,
  scrollStagger,
  scrollChildStagger,
  scrollItem,
} from './aboutScrollMotion';
import styles from './AboutPage.module.css';

const scrollViewport = aboutScrollViewport;

const MISSION_PILLARS = [
  { icon: Target, label: 'Reliable', detail: 'Dependable supply' },
  { icon: Sparkles, label: 'Innovative', detail: 'Modern grades' },
  { icon: Recycle, label: 'Sustainable', detail: 'Responsible focus' },
];

const VISION_PILLARS = [
  { icon: Award, label: 'Leadership', detail: 'Industry trust' },
  { icon: Users, label: 'Customers', detail: 'Lasting satisfaction' },
  { icon: Sparkles, label: 'Innovation', detail: 'Continuous growth' },
];

const INTRO_SECTORS = [
  'Food packaging',
  'Beverage grades',
  'Retail supply',
  'Industrial rolls',
];

const WHO_WE_ARE = [
  {
    icon: Award,
    title: 'Registered in 2018',
    text: 'Built on a strong foundation of compliance, trust, and long-term partnerships.',
  },
  {
    icon: Factory,
    title: 'Packaging Industry Experience',
    text: 'Deep expertise across food, beverage, retail, and industrial paper applications.',
  },
  {
    icon: Wrench,
    title: 'Manufacturer & Supplier',
    text: 'End-to-end production and reliable supply of paper-based packaging materials.',
  },
  {
    icon: HeartHandshake,
    title: 'Customer-Focused Approach',
    text: 'Custom solutions, responsive support, and delivery aligned with your timelines.',
  },
];

const MANUFACTURING = [
  { icon: Sparkles, label: 'Modern machinery', detail: 'High-speed lines & precision converting' },
  { icon: ShieldCheck, label: 'Quality checks', detail: 'In-process testing on every batch' },
  { icon: Users, label: 'Skilled workforce', detail: 'Experienced production & QC teams' },
  { icon: Target, label: 'Consistent standards', detail: 'Stable GSM, finish & roll quality' },
];

const INTRO_HIGHLIGHTS = [
  { icon: Building2, label: 'Founded 2018', detail: 'Registered manufacturer & supplier' },
  { icon: MapPin, label: 'Meerut, UP', detail: 'Production close to major corridors' },
  { icon: Package, label: 'Paper packaging', detail: 'Food, retail & industrial grades' },
];

const SUSTAIN_HIGHLIGHTS = [
  { icon: Sparkles, label: 'Less waste', detail: 'per production run' },
  { icon: Recycle, label: 'Recyclable', detail: 'grade options' },
  { icon: Leaf, label: 'Responsible', detail: 'material choices' },
];

const SUSTAINABILITY = [
  {
    icon: Leaf,
    label: 'Eco-conscious materials',
    detail: 'Responsible fibre choices and paper grades',
  },
  {
    icon: Sparkles,
    label: 'Efficient production',
    detail: 'Optimised runs with less waste per order',
  },
  {
    icon: Recycle,
    label: 'Recyclable options',
    detail: 'Grades aligned with recovery streams',
  },
];

const FACTORY_IMAGES = [
  {
    src: '/images/hero-section.jpeg',
    alt: 'Greeneri operations and supply',
    caption: 'Operations',
  },
  {
    src: '/images/products/bobbins.jpeg',
    alt: 'Paper bobbins and jumbo rolls',
    caption: 'Roll stock',
  },
  {
    src: '/images/products/packaging1 (1).png',
    alt: 'Packaging materials and finished grades',
    caption: 'Packaging grades',
  },
];

const CTA_PERKS = [
  { icon: Package, text: 'Fast quotes on specs & volume' },
  { icon: ShieldCheck, text: 'QC on every production batch' },
  { icon: MapPin, text: 'Reliable supply from Meerut, UP' },
];

const handleCatalogDownload = () => {
  const catalogPath = '/catalog/greeneripapers-catalog.pdf';
  const link = document.createElement('a');
  link.href = catalogPath;
  link.download = 'greeneripapers-catalog.pdf';
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const AboutPage = () => (
  <main className={styles.page}>
    <AboutHero />

    {/* —— Company Introduction —— */}
    <ScrollRevealSection className={styles.intro} aria-labelledby="intro-title">
      <div className={styles.container}>
        <motion.div
          className={styles.introStage}
          variants={scrollStagger}
          initial="hidden"
          whileInView="visible"
          viewport={scrollViewport}
        >
          <motion.div className={styles.introSplit} variants={scrollItem}>
            <div className={styles.introSpotlight} aria-hidden="true" />
            <div className={styles.introGrain} aria-hidden="true" />

            <div className={styles.introBrandPane}>
              <span className={styles.introBadge}>Our story</span>
              <h2 id="intro-title" className={styles.introBrandTitle}>
                Company
                <span>Introduction</span>
              </h2>
              <p className={styles.introBrandLine}>
                Manufacturer &amp; supplier of paper packaging — Meerut, India.
              </p>
              <div className={styles.introSince} aria-label="Established 2018">
                <span className={styles.introSinceLabel}>Since</span>
                <span className={styles.introSinceYear}>2018</span>
              </div>
            </div>

            <div className={styles.introCopyPane}>
              <p className={styles.introLead}>
                Established in <strong>2018</strong>, Greeneri Paper Tradelinks is a trusted
                manufacturer and supplier of paper-based packaging products and raw materials.
              </p>
              <p className={`u-body ${styles.introText}`}>
                We deliver consistent grades and dependable supply for food, beverage, retail, and
                industrial lines — with responsive service for teams that run on paper every day.
              </p>
              <div className={styles.introSectors} aria-label="Industries we serve">
                <span className={styles.introSectorsHead}>We serve</span>
                <ul className={styles.introSectorList}>
                  {INTRO_SECTORS.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.ul
            className={styles.introMetrics}
            variants={scrollChildStagger}
          >
            {INTRO_HIGHLIGHTS.map((item) => (
              <motion.li key={item.label} variants={scrollItem}>
                <span className={styles.introMetricIcon}>
                  <item.icon size={22} strokeWidth={2} aria-hidden="true" />
                </span>
                <div className={styles.introMetricCopy}>
                  <strong>{item.label}</strong>
                  <span>{item.detail}</span>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>
      </div>
    </ScrollRevealSection>

    {/* —— Who We Are —— */}
    <ScrollRevealSection className={styles.who} aria-labelledby="who-title">
      <div className={styles.whoBackdrop} aria-hidden="true">
        <div className={styles.whoBgBase} />
        <div className={styles.whoBgCornerTL} />
        <div className={styles.whoBgCornerBR} />
        <div className={styles.whoBgCenter} />
        <div className={styles.whoBgGrain} />
      </div>
      <div className={styles.container}>
        <motion.div
          className={styles.whoInner}
          variants={scrollStagger}
          initial="hidden"
          whileInView="visible"
          viewport={scrollViewport}
        >
          <motion.header className={styles.whoTop} variants={scrollItem}>
            <span className={styles.whoKicker}>Who we are</span>
            <h2 id="who-title" className={styles.whoTitle}>
              <span className={styles.whoTitleDark}>What defines</span>{' '}
              <span className={styles.whoTitleMark}>our company</span>
            </h2>
            <p className={styles.whoLead}>
              Quality paper, dependable supply, and partnerships built for the long run.
            </p>
          </motion.header>

          <motion.ol
            className={`${styles.whoTimelineWrap} ${styles.whoTimeline}`}
            variants={scrollChildStagger}
          >
              {WHO_WE_ARE.map((item) => (
                <motion.li key={item.title} className={styles.whoStep} variants={scrollItem}>
                  <span className={styles.whoStepMarker}>
                    <item.icon size={20} strokeWidth={2} aria-hidden="true" />
                  </span>
                  <div className={styles.whoStepBody}>
                    <h3 className={styles.whoStepTitle}>{item.title}</h3>
                    <p className={styles.whoStepText}>{item.text}</p>
                  </div>
                </motion.li>
              ))}
          </motion.ol>
        </motion.div>
      </div>
    </ScrollRevealSection>

    {/* —— Mission & Vision —— */}
    <ScrollRevealSection className={styles.missionVision} aria-labelledby="mv-section-title">
      <div className={styles.container}>
        <motion.div
          variants={scrollStagger}
          initial="hidden"
          whileInView="visible"
          viewport={scrollViewport}
        >
          <motion.header className={styles.mvHeadCompact} variants={scrollItem}>
            <h2 id="mv-section-title" className={styles.mvHeadCompactTitle}>
              Our <span>Mission &amp; Vision</span>
            </h2>
          </motion.header>

          <motion.div className={styles.mvElegant} variants={scrollItem}>
            <div className={styles.mvElegantGlow} aria-hidden="true" />

          <article
            className={styles.mvBlockMission}
            aria-labelledby="mission-title"
          >
            <span className={styles.mvWatermark} aria-hidden="true">
              M
            </span>
            <header className={styles.mvBlockHead}>
              <span className={styles.mvIconMission}>
                <Target size={24} strokeWidth={2} aria-hidden="true" />
              </span>
              <div>
                <p className={styles.mvBlockLabel}>Our Mission</p>
                <h3 id="mission-title" className={styles.mvBlockTitle}>
                  Reliable. Innovative. Sustainable.
                </h3>
              </div>
            </header>
            <p className={styles.mvBlockText}>
              To provide reliable, innovative, and sustainable paper packaging solutions while
              maintaining the highest quality standards on every order. We focus on consistent grades,
              dependable supply, and responsive service — so each delivery strengthens your product,
              your production line, and your brand in the market.
            </p>
            <ul className={styles.mvFeatGrid} aria-label="Mission pillars">
              {MISSION_PILLARS.map((item) => (
                <li key={item.label}>
                  <span className={styles.mvFeatIcon}>
                    <item.icon size={18} strokeWidth={2.25} aria-hidden="true" />
                  </span>
                  <span className={styles.mvFeatCopy}>
                    <strong>{item.label}</strong>
                    <em>{item.detail}</em>
                  </span>
                </li>
              ))}
            </ul>
          </article>

          <div className={styles.mvBridge} aria-hidden="true">
            <span className={styles.mvBridgeLine} />
            <span className={styles.mvBridgeDot} />
            <span className={styles.mvBridgeLine} />
          </div>

          <article
            className={styles.mvBlockVision}
            aria-labelledby="vision-title"
          >
            <span className={styles.mvWatermark} aria-hidden="true">
              V
            </span>
            <header className={styles.mvBlockHead}>
              <span className={styles.mvIconVision}>
                <Award size={24} strokeWidth={2} aria-hidden="true" />
              </span>
              <div>
                <p className={styles.mvBlockLabel}>Our Vision</p>
                <h3 id="vision-title" className={styles.mvBlockTitle}>
                  Leading through <span>quality</span>
                </h3>
              </div>
            </header>
            <p className={styles.mvBlockText}>
              To become a leading name in the paper packaging industry through quality products,
              customer satisfaction, and continuous innovation. We aim to grow alongside the food,
              beverage, retail, and industrial businesses we serve — building long-term partnerships
              across India and in export markets beyond.
            </p>
            <ul className={styles.mvFeatGrid} aria-label="Vision pillars">
              {VISION_PILLARS.map((item) => (
                <li key={item.label}>
                  <span className={styles.mvFeatIcon}>
                    <item.icon size={18} strokeWidth={2.25} aria-hidden="true" />
                  </span>
                  <span className={styles.mvFeatCopy}>
                    <strong>{item.label}</strong>
                    <em>{item.detail}</em>
                  </span>
                </li>
              ))}
            </ul>
          </article>
          </motion.div>
        </motion.div>
      </div>
    </ScrollRevealSection>

    {/* —— Manufacturing & Quality —— */}
    <ScrollRevealSection className={styles.manufacturing} aria-labelledby="mfg-title">
      <div className={styles.mfgMesh} aria-hidden="true" />
      <div className={styles.mfgGlowOrange} aria-hidden="true" />
      <div className={styles.mfgGlowGreen} aria-hidden="true" />
      <span className={styles.mfgWatermark} aria-hidden="true">
        MFG
      </span>

      <div className={styles.container}>
        <motion.div
          className={styles.mfgAtelier}
          variants={scrollStagger}
          initial="hidden"
          whileInView="visible"
          viewport={scrollViewport}
        >
          <motion.header className={styles.mfgHeroHead} variants={scrollItem}>
            <h2 id="mfg-title" className={styles.mfgHeroTitle}>
              Manufacturing &amp; <span>Quality</span>
            </h2>
            <p className={styles.mfgHeroLead}>
              Built for scale, checked at every stage — from roll stock to finished packaging grades.
            </p>
            <div className={styles.mfgHeroRule} aria-hidden="true">
              <span />
              <span />
            </div>
          </motion.header>

          <motion.div className={styles.mfgEditorial} variants={scrollChildStagger}>
            <motion.div
              className={styles.mfgGallery}
              variants={scrollItem}
              aria-label="Facility photos"
            >
              <div className={styles.mfgGalleryRing} aria-hidden="true" />
              {FACTORY_IMAGES.map((img, index) => {
                const polaroidClass =
                  index === 0
                    ? styles.mfgPolaroid1
                    : index === 1
                      ? styles.mfgPolaroid2
                      : styles.mfgPolaroid3;
                return (
                <figure
                  key={img.src}
                  className={`${styles.mfgPolaroid} ${polaroidClass}`}
                >
                  <img src={img.src} alt={img.alt} loading="lazy" />
                  <figcaption>{img.caption}</figcaption>
                </figure>
                );
              })}
              <div className={styles.mfgQCBadge}>
                <ShieldCheck size={15} strokeWidth={2.5} aria-hidden="true" />
                <span>QC at every stage</span>
              </div>
            </motion.div>

            <motion.div className={styles.mfgStory} variants={scrollItem}>
              <p className={styles.mfgStoryLead}>
                Our Meerut facility pairs modern converting lines with disciplined quality control —
                so every roll and sheet meets what your production line demands.
              </p>
              <p className={styles.mfgStoryText}>
                From jumbo stock to finished grades, we monitor GSM, finish, and consistency
                shipment after shipment — stable output you can plan around.
              </p>
            </motion.div>

            <motion.ul
              className={styles.mfgTiles}
              variants={scrollChildStagger}
              aria-label="Manufacturing capabilities"
            >
              {MANUFACTURING.map((item, index) => (
                <motion.li
                  key={item.label}
                  className={index % 2 === 0 ? styles.mfgTileGreen : styles.mfgTileOrange}
                  variants={scrollItem}
                >
                  <span className={styles.mfgTileAccent} aria-hidden="true" />
                  <span className={styles.mfgTileIcon}>
                    <item.icon size={20} strokeWidth={2.25} aria-hidden="true" />
                  </span>
                  <div className={styles.mfgTileCopy}>
                    <strong>{item.label}</strong>
                    <span>{item.detail}</span>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        </motion.div>
      </div>
    </ScrollRevealSection>

    {/* —— Sustainability —— */}
    <ScrollRevealSection className={styles.sustain} aria-labelledby="sustain-title">
      <div className={styles.sustainCurveTop} aria-hidden="true" />
      <div className={styles.sustainAuraMint} aria-hidden="true" />
      <div className={styles.sustainAuraLeaf} aria-hidden="true" />
      <span className={styles.sustainWatermark} aria-hidden="true">
        ECO
      </span>

      <div className={styles.container}>
        <motion.div
          className={styles.sustainEco}
          variants={scrollStagger}
          initial="hidden"
          whileInView="visible"
          viewport={scrollViewport}
        >
          <motion.div className={styles.sustainCopy} variants={scrollItem}>
            <h2 id="sustain-title" className={styles.sustainTitle}>
              Responsible packaging for a <span>greener tomorrow</span>
            </h2>
            <p className={styles.sustainText}>
              We promote environmentally responsible packaging through thoughtful materials and
              efficient manufacturing — helping brands lower impact without sacrificing strength
              or presentation.
            </p>
            <ul className={styles.sustainHighlights} aria-label="Sustainability focus">
              {SUSTAIN_HIGHLIGHTS.map((item, index) => {
                const highlightClass =
                  index === 0
                    ? styles.sustainHighlightGreen
                    : index === 1
                      ? styles.sustainHighlightTeal
                      : styles.sustainHighlightWarm;
                return (
                  <li key={item.label} className={highlightClass}>
                    <span className={styles.sustainHighlightIcon}>
                      <item.icon size={16} strokeWidth={2.35} aria-hidden="true" />
                    </span>
                    <div className={styles.sustainHighlightCopy}>
                      <strong>{item.label}</strong>
                      <span>{item.detail}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </motion.div>

          <motion.div
            className={styles.sustainOrbit}
            variants={scrollChildStagger}
            aria-label="Sustainability pillars"
          >
            <div className={styles.sustainRingWrap}>
              <div className={styles.sustainRingOuter} aria-hidden="true" />
              <div className={styles.sustainRingMid} aria-hidden="true" />
              <div className={styles.sustainRingCore}>
                <Leaf size={36} strokeWidth={1.75} aria-hidden="true" />
              </div>
            </div>

            <ul className={styles.sustainDeck}>
              {SUSTAINABILITY.map((item, index) => {
                const cardClass =
                  index === 0
                    ? styles.sustainCard1
                    : index === 1
                      ? styles.sustainCard2
                      : styles.sustainCard3;
                return (
                  <motion.li
                    key={item.label}
                    className={`${styles.sustainCard} ${cardClass}`}
                    variants={scrollItem}
                  >
                    <span className={styles.sustainCardIcon}>
                      <item.icon size={20} strokeWidth={2.25} aria-hidden="true" />
                    </span>
                    <div className={styles.sustainCardCopy}>
                      <strong>{item.label}</strong>
                      <span>{item.detail}</span>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </ScrollRevealSection>

    {/* —— Contact CTA —— */}
    <ScrollRevealSection className={styles.cta} aria-labelledby="cta-title">
      <div className={styles.container}>
        <motion.article
          className={styles.ctaImpact}
          variants={scrollStagger}
          initial="hidden"
          whileInView="visible"
          viewport={scrollViewport}
        >
          <div className={styles.ctaImpactOrb} aria-hidden="true" />
          <div className={styles.ctaImpactGrid} aria-hidden="true" />

          <div className={styles.ctaImpactInner}>
            <motion.div className={styles.ctaImpactLead} variants={scrollItem}>
              <h2 id="cta-title" className={styles.ctaTitle}>
                Need Packaging <span>Solutions?</span>
              </h2>
              <p className={styles.ctaSub}>
                Tell us your product, volume, and timeline — we&apos;ll help you choose the right
                paper grade, finish, and delivery plan for your production line.
              </p>
              <ul className={styles.ctaImpactPerks} aria-label="Why contact us">
                {CTA_PERKS.map((item) => (
                  <li key={item.text}>
                    <item.icon size={14} strokeWidth={2.35} aria-hidden="true" />
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div className={styles.ctaImpactAside} variants={scrollItem}>
              <div className={styles.ctaImpactActions} aria-label="Actions">
                <span className={styles.ctaActionLabel}>Get started</span>
                <Link to="/contact" className={styles.ctaImpactPrimary}>
                  Get Quote
                  <ArrowUpRight size={17} strokeWidth={2.5} aria-hidden="true" />
                </Link>
                <button
                  type="button"
                  className={styles.ctaImpactSecondary}
                  onClick={handleCatalogDownload}
                >
                  <Download size={17} strokeWidth={2.35} aria-hidden="true" />
                  Download Catalog
                </button>
              </div>

              <div className={styles.ctaImpactReach} aria-label="Contact">
                <span className={styles.ctaReachLabel}>Direct contact</span>
                <a href="tel:+919557005492" className={styles.ctaImpactChip}>
                  <span className={styles.ctaChipIcon}>
                    <Phone size={16} strokeWidth={2.35} aria-hidden="true" />
                  </span>
                  <span className={styles.ctaChipText}>
                    <em>Call us</em>
                    <strong>+91 95570 05492</strong>
                  </span>
                </a>
                <a href="mailto:info@greeneripapers.com" className={styles.ctaImpactChip}>
                  <span className={styles.ctaChipIcon}>
                    <Mail size={16} strokeWidth={2.35} aria-hidden="true" />
                  </span>
                  <span className={styles.ctaChipText}>
                    <em>Email</em>
                    <strong>info@greeneripapers.com</strong>
                  </span>
                </a>
              </div>
            </motion.div>
          </div>
        </motion.article>
      </div>
    </ScrollRevealSection>
  </main>
);

export default AboutPage;
